/**
 * Pure message dispatch for WebSocket messages from the live-session worker.
 *
 * This module contains no React, no hooks, and no state. It takes a parsed
 * message object and a bag of handler callbacks, then dispatches to the
 * appropriate one. Testable in isolation.
 *
 * JSON.parse and error handling live in the caller (useSessionWebSocket),
 * not here -- this function only receives already-parsed data.
 */

import type { SegmentKind, ServerErrorCode } from './protocol-types';
import { parseRespondentConversationState, type RespondentConversationState } from '../../lib/multimodal-contract';
import type { MountedInformationBlockMetadata } from './hooks/useInformationBlockMount';

const SERVER_ERROR_CODES = new Set<ServerErrorCode>([
  'conversation_unavailable',
  'service_unavailable',
  'realtime_unavailable',
]);

export interface MessageHandlers {
  /** Public response projection, shared with the non-media thread. */
  onConversationState?: (state: RespondentConversationState) => void;
  onReady: (brandLogoUrl?: string | null) => void;
  /**
   * Worker heartbeat ping.
   *
   * @param timestamp - The ping's server-clock timestamp, echoed back in the
   *   pong so the worker can estimate the client-minus-server clock offset.
   *   Undefined for a malformed ping.
   */
  onPing: (timestamp?: number) => void;
  onConnected: () => void;
  onVideoHeartbeatAck: (warning?: string) => void;
  onInactivityWarning: (countdown: number) => void;
  onInactivityCanceled: () => void;
  onKicked: (reason: string) => void;
  onError: (code: ServerErrorCode, terminal: boolean, retryable: boolean) => void;
  onSessionDurationWarning: (remainingSeconds: number) => void;
  onSessionRefreshed: () => void;
  onReconnecting: (attempt: number, maxAttempts: number) => void;
  /** Worker has accepted the rejoin and applied a provider-neutral mic gate. */
  onReconnected: (
    upstreamSession: 'preserved' | 'recreated',
    microphoneGate: 'open_immediately' | 'wait_for_assistant_output',
  ) => void;
  onDisconnected: () => void;
  onInterviewEnding: (countdown: number) => void;
  onInterviewEnded: () => void;
  onRealtimeSessionReady: (microphoneSampleRateHz: number, preferredChunkDurationMs: number) => void;
  onRealtimeTranscript: (speaker: 'user' | 'ai', text: string, final: boolean, itemId?: string) => void;
  onRealtimeAudio: (data: string, sampleRateHz: number, itemId: string, contentIndex: number, providerGeneration?: number) => Promise<void>;
  onRealtimeResponseState: (state: 'started' | 'completed', itemId?: string) => void;
  onRealtimeInterrupted: (itemId?: string) => void;
  onRealtimeToolState: (
    callId: string,
    name: string,
    state: 'called' | 'completed' | 'failed',
    timestamp: number,
    responseItemId?: string,
    args?: Record<string, unknown>,
    result?: Record<string, unknown>,
  ) => void;
  onScreenShareRequested: (reason: string) => void;
  onScreenShareEndRequested: (reason: string) => void;
  /**
   * Worker is asking a surviving continuous recorder to stop and restart so the
   * next segment opens on a header-bearing chunk 0 (reconnect segment boundary,
   * DEFECT 2). Ignored by the hook when no recorder survives (page reload).
   *
   * @param kind - The recording kind the worker is tracking.
   */
  onRestartRecording: (kind: SegmentKind) => void;
  /**
   * Worker is mounting an information-block image on the respondent's screen
   * (the show_information_block tool, or the best-effort reconnect re-mount).
   *
   * @param fieldName - snake_case field name of the block.
   * @param imageUrl - relative public serve URL the client fetches.
   * @param title - optional headline used as the accessible label.
   */
  onMountInformationBlock: (
    fieldName: string,
    imageUrl: string,
    title?: string,
    metadata?: MountedInformationBlockMetadata,
  ) => void;
  /**
   * Worker is unmounting the information-block image (scoped interrupt of the
   * mount-triggering turn).
   *
   * @param fieldName - snake_case field name of the block to unmount.
   */
  onUnmountInformationBlock: (fieldName: string) => void;
  onUnknown: (type: string, data: unknown) => void;
}

/**
 * Route a parsed WebSocket message to the appropriate handler.
 *
 * @param wsData - The already-parsed JSON message from the worker.
 * @param handlers - Callback bag provided by the hook that owns state.
 */
export async function routeMessage(
  wsData: Record<string, unknown>,
  handlers: MessageHandlers,
): Promise<void> {
  const type = wsData.type;

  if (typeof type !== 'string') {
    handlers.onUnknown(String(wsData.type), wsData);
    return;
  }

  switch (type) {
    case 'conversation_state': {
      const state = parseRespondentConversationState(wsData.state);
      if (state) handlers.onConversationState?.(state);
      return;
    }
    case 'ready':
      handlers.onReady((wsData as Record<string, unknown>).brandLogoUrl as string | null | undefined);
      return;

    case 'ping':
      handlers.onPing(typeof wsData.timestamp === 'number' ? wsData.timestamp : undefined);
      return;

    case 'video_heartbeat_ack':
      handlers.onVideoHeartbeatAck(wsData.warning as string | undefined);
      return;

    case 'connected':
      handlers.onConnected();
      return;

    case 'inactivity_warning':
      handlers.onInactivityWarning((wsData.countdown as number) || 30);
      return;

    case 'inactivity_canceled':
      handlers.onInactivityCanceled();
      return;

    case 'kicked':
      handlers.onKicked((wsData.reason as string) || 'Disconnected for inactivity');
      return;

    case 'error': {
      const hasKnownCode = typeof wsData.code === 'string'
        && SERVER_ERROR_CODES.has(wsData.code as ServerErrorCode);
      const errorCode = hasKnownCode
        ? wsData.code as ServerErrorCode
        : 'service_unavailable';
      handlers.onError(
        errorCode,
        hasKnownCode ? wsData.terminal !== false : true,
        hasKnownCode ? wsData.retryable !== false : true,
      );
      return;
    }

    case 'session_duration_warning':
      handlers.onSessionDurationWarning(wsData.remainingSeconds as number);
      return;

    case 'session_refreshed':
      handlers.onSessionRefreshed();
      return;

    case 'reconnecting':
      handlers.onReconnecting(
        wsData.attempt as number,
        wsData.maxAttempts as number,
      );
      return;

    case 'reconnected':
      handlers.onReconnected(
        wsData.upstreamSession === 'preserved' ? 'preserved' : 'recreated',
        wsData.microphoneGate === 'open_immediately'
          ? 'open_immediately'
          : 'wait_for_assistant_output',
      );
      return;

    case 'disconnected':
      handlers.onDisconnected();
      return;

    case 'interview_ending':
      handlers.onInterviewEnding(
        typeof wsData.countdown === 'number' ? wsData.countdown : 3,
      );
      return;

    case 'call_ended':
    case 'interview_ended':
      handlers.onInterviewEnded();
      return;

    case 'issue_reported':
      // Acknowledgment only -- no client action
      return;

    case 'realtime_session_ready':
      handlers.onRealtimeSessionReady(
        wsData.microphoneSampleRateHz as number,
        wsData.preferredChunkDurationMs as number,
      );
      return;

    case 'realtime_transcript':
      handlers.onRealtimeTranscript(
        wsData.speaker === 'user' ? 'user' : 'ai',
        (wsData.text as string) || '',
        wsData.final === true,
        wsData.itemId as string | undefined,
      );
      return;

    case 'realtime_audio':
      await handlers.onRealtimeAudio(
        (wsData.data as string) || '',
        wsData.sampleRateHz as number,
        (wsData.itemId as string) || '',
        (wsData.contentIndex as number) || 0,
        typeof wsData.providerGeneration === 'number' ? wsData.providerGeneration : undefined,
      );
      return;

    case 'realtime_response_state':
      handlers.onRealtimeResponseState(
        wsData.state === 'completed' ? 'completed' : 'started',
        wsData.itemId as string | undefined,
      );
      return;

    case 'realtime_interrupted':
      handlers.onRealtimeInterrupted(wsData.itemId as string | undefined);
      return;

    case 'realtime_tool_state':
      handlers.onRealtimeToolState(
        (wsData.callId as string) || '',
        (wsData.name as string) || '',
        wsData.state === 'completed' || wsData.state === 'failed' ? wsData.state : 'called',
        typeof wsData.timestamp === 'number' ? wsData.timestamp : 0,
        wsData.responseItemId as string | undefined,
        wsData.arguments as Record<string, unknown> | undefined,
        wsData.result as Record<string, unknown> | undefined,
      );
      return;

    case 'screen_share_requested':
      handlers.onScreenShareRequested((wsData.reason as string) || '');
      return;

    case 'screen_share_end_requested':
      handlers.onScreenShareEndRequested((wsData.reason as string) || '');
      return;

    case 'restart_recording': {
      // The wire `kind` is untyped; validate against the known set instead of an
      // unchecked cast so a malformed value never propagates as a SegmentKind.
      const kind: SegmentKind = wsData.kind === 'screen' || wsData.kind === 'audio' ? wsData.kind : 'camera';
      handlers.onRestartRecording(kind);
      return;
    }

    case 'mount_information_block':
      handlers.onMountInformationBlock(
        (wsData.fieldName as string) || '',
        (wsData.imageUrl as string) || '',
        wsData.title as string | undefined,
        {
          imageStillUrl: wsData.imageStillUrl as string | undefined,
          imageSource: wsData.imageSource === 'klipy' || wsData.imageSource === 'upload'
            ? wsData.imageSource
            : undefined,
        },
      );
      return;

    case 'unmount_information_block':
      handlers.onUnmountInformationBlock((wsData.fieldName as string) || '');
      return;

    default:
      handlers.onUnknown(type, wsData);
      return;
  }
}
